import type { Terminal } from '../terminal';
import type { GameState, Strike, StrikeResult } from '../../types/game-state';
import type { MapConfig, MissileArc } from '../components/world-map';
import { drawMap, animateMissiles, showMap, hideMap } from '../components/world-map';
import type { AudioSystem } from '../audio';

async function runLaunch(
  terminal: Terminal,
  state: GameState,
  strike: Strike,
  results: readonly StrikeResult[],
  mapConfig: MapConfig | null,
  audio: AudioSystem | null,
): Promise<void> {
  await terminal.println('');
  await terminal.println('════════════════════════════════════════════════════════════════');
  await terminal.println('  LAUNCH SEQUENCE INITIATED', { cssClass: 'danger' });
  await terminal.println('════════════════════════════════════════════════════════════════');
  await terminal.println('');

  // Update DEFCON display
  const defconEl = document.getElementById('defcon-display');
  if (defconEl) {
    defconEl.className = `defcon-display visible defcon-${state.defconLevel}`;
    defconEl.textContent = `DEFCON ${state.defconLevel}`;
  }

  audio?.klaxon();

  // Launch countdown
  await terminal.println('  LAUNCH AUTHORIZATION CODE: CONFIRMED', { cssClass: 'danger' });
  await terminal.pause(300);
  await terminal.println('  STRATEGIC COMMAND: EXECUTE', { cssClass: 'danger' });
  await terminal.pause(300);
  await terminal.println('');

  for (let i = 10; i >= 1; i--) {
    await terminal.print(`  T-${String(i).padStart(2, '0')} `, { speed: 0 });
    await terminal.println('\u2588'.repeat(i), { speed: 0, cssClass: 'danger' });
    audio?.tick();
    await terminal.pause(200);
  }

  await terminal.println('');
  await terminal.println('  \u2588\u2588\u2588\u2588  MISSILES AWAY  \u2588\u2588\u2588\u2588', { cssClass: 'danger' });
  await terminal.println('');
  await terminal.pause(400);

  // Show world map with missile trajectories
  if (mapConfig) {
    showMap(mapConfig.canvas);
    drawMap(mapConfig, state);

    // Build missile arcs from attacker bases to targets
    const arcs: MissileArc[] = [];
    const attackerInstallations = strike.attackerSide === 'US'
      ? state.world.usInstallations
      : state.world.ussrInstallations;
    const targetCities = strike.attackerSide === 'US'
      ? state.world.ussrCities
      : state.world.usCities;
    const targetInstallations = strike.attackerSide === 'US'
      ? state.world.ussrInstallations
      : state.world.usInstallations;

    const attackerArsenal = strike.attackerSide === 'US' ? state.usArsenal : state.ussrArsenal;

    for (const assignment of strike.assignments) {
      const weapon = attackerArsenal.weapons.find(w => w.id === assignment.weaponId);
      const base = attackerInstallations.find(i => i.id === weapon?.baseId);

      let targetCoord;
      if (assignment.targetType === 'city') {
        const city = targetCities.find(c => c.id === assignment.targetId);
        targetCoord = city?.coord;
      } else {
        const inst = targetInstallations.find(i => i.id === assignment.targetId);
        targetCoord = inst?.coord;
      }

      if (base && targetCoord) {
        arcs.push({
          from: base.coord,
          to: targetCoord,
          color: strike.attackerSide === 'US' ? '#33ff33' : '#ff3333',
          progress: 0,
        });
      }
    }

    // Animate and wait for completion
    if (arcs.length > 0) {
      await new Promise<void>((resolve) => {
        animateMissiles(mapConfig, state, arcs, () => {
          audio?.detonation();
          resolve();
        });
      });
      await terminal.pause(1500);
    }

    hideMap(mapConfig.canvas);
    await terminal.pause(700); // wait for fade-out transition
  }

  // Show launch details
  await terminal.println('  TRACKING DELIVERY VEHICLES...', { cssClass: 'warning' });
  await terminal.pause(500);

  const uniqueTargets = new Map<string, StrikeResult>();
  for (const result of results) {
    const existing = uniqueTargets.get(result.targetId);
    if (!existing || result.casualties > existing.casualties) {
      uniqueTargets.set(result.targetId, result);
    }
  }

  for (const result of uniqueTargets.values()) {
    const status = result.targetDestroyed ? 'DESTROYED' : 'HIT';
    const statusClass = result.targetDestroyed ? 'danger' : 'warning';
    await terminal.println(`    ${result.targetName.padEnd(24)} ... ${status}`, { cssClass: statusClass, speed: 15 });
    await terminal.pause(100);
  }

  await terminal.println('');

  // Summary
  const totalCasualties = results.reduce((sum, r) => sum + r.casualties, 0);
  const destroyed = results.filter(r => r.targetDestroyed).length;

  await terminal.println(`  DETONATIONS CONFIRMED: ${results.filter(r => r.weaponDetonated).length}`, { cssClass: 'bright' });
  await terminal.println(`  TARGETS DESTROYED: ${destroyed}`, { cssClass: 'bright' });
  if (totalCasualties > 0) {
    await terminal.println(`  ESTIMATED CASUALTIES: ${totalCasualties.toLocaleString()}`, { cssClass: 'danger' });
  }
  await terminal.println('');
  await terminal.pause(1000);
}

export { runLaunch };
